<template>
  <v-card class="mb-3">
    <v-card-title>
      <span>Usuários Atribuídos</span>
    </v-card-title>
    <v-card-text>
      <v-row v-show="!readonly">
        <v-col cols="3">
          <v-text-field
            v-model="form.id"
            label="ID"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="fetch"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="form.email"
            label="Email"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="fetch"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            :model-value="form.document"
            label="Documento"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="fetch"
            @update:model-value="form.document = ($event ?? '').toUpperCase()"
          />
        </v-col>
        <v-col cols="3">
          <v-btn
            :loading="$loader.loading"
            block
            color="primary"
            @click="fetch"
          >
            Buscar
          </v-btn>
        </v-col>
      </v-row>

      <v-table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Nome</th>
            <th>Documento</th>
            <th>Email</th>
            <th>Atribuído</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in pagination.items" :key="user.id">
            <td>{{ user.id.split("-")[0] }}</td>
            <td>{{ user.name }}</td>
            <td>{{ $formatters.document(user.document, user.document_type) }}</td>
            <td>{{ user.email }}</td>
            <td class="text-center">
              <v-checkbox
                :model-value="isAssigned(user)"
                hide-details
                multiple
                :readonly="readonly"
                @update:model-value="onCheck(user)"
              />
            </td>
          </tr>
        </tbody>
      </v-table>
      <v-pagination
        v-model="pagination.page"
        :total-visible="5"
        :length="pagination.totalPages"
        @update:model-value="fetch"
      />
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { AxiosError } from 'axios'
import Coupon from '@/types/Coupon'
import User from '@/types/User'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import CouponService from '@/services/CouponService'

interface Props {
  readonly?: boolean
}

interface Form {
  id: string
  email: string
  document: string
  file?: File
}

interface Pagination {
  items: User[]
  page: number
  itemsPerPage: number
  totalPages: number
}

const loader = useLoader()
const snackbar = useSnackBar()

const service = new CouponService()

const emit = defineEmits<{
  check: [users: string[]]
}>()

const { readonly } = defineProps<Props>()

const model = defineModel<Coupon>({ required: true })

const form = ref<Form>({ id: '', email: '', document: '' })

const pagination = ref<Pagination>({ items: [], page: 1, itemsPerPage: 5, totalPages: 0 })

const unassigned = ref<string[]>([])

function isAssigned(user: User) {
  return !unassigned.value.some(id => [user.id, user.email, user.document].includes(id))
}

function onCheck(user: User) {
  if (unassigned.value.some(id => id === user.id)) {
    unassigned.value = unassigned.value.filter(id => id !== user.id)
  } else {
    unassigned.value.push(user.id)
  }
  emit('check', unassigned.value)
}

async function fetch() {
  loader.increment()
  try {
    const query = new URLSearchParams([
      ['id', form.value.id],
      ['email', form.value.email],
      ['document', form.value.document],
    ].map(v => [v[0], v[1].trim()]).filter(v => !!v[1]))

    const response = await service.users(model.value.id, pagination.value.page, query.toString(), pagination.value.itemsPerPage)

    pagination.value.items = response.data.data
    pagination.value.page = response.data.current_page ?? 1
    pagination.value.totalPages = response.data.last_page ?? 1
  } catch (error) {
    if (error instanceof AxiosError) {
      snackbar.show(error.response?.data?.message, 'error')
    } else if (error instanceof Error) {
      snackbar.show(error.message, 'error')
    } else {
      snackbar.show('Não foi possível carregar os usuários', 'error')
    }
    console.log(error)
  } finally {
    loader.decrement()
  }
}

onMounted(() => {
  fetch()
})

</script>
